import * as React from 'react';
import {
    DataGrid,
    getGridStringOperators,
    getGridSingleSelectOperators,
    GridActionsCellItem,
} from '@mui/x-data-grid';
import { Button } from '@mui/material';
import { makeStyles } from "@mui/styles";
import { useList } from "hooks/useList";
import { formatDate } from 'utils/date';
import { getListActions } from 'utils';
import { deleteCodeChallengeById, getCodeChallenges } from 'services/codeChallengeService';

import Box from '@mui/material/Box';
import ContentHeader from 'components/ContentHeader';
import GridToolbar from 'components/GridToolbar';
import ListPagination from 'components/ListPagination';
import LinearProgress from '@mui/material/LinearProgress';
import ImportChallengeDialog from 'components/dialogs/ImportChallengesDialog';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';

const useStyles = makeStyles((theme) => ({
    tableHeader: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
    }
}));

const BaseFilters = {
    include: ["user", "tags"],
    includeTotal: true,
    orderBy: "createdAt"
};

export default function CodingQuestions() {
    const classes = useStyles();
    const [openDialog, setOpenDialog] = React.useState(false);
    const [activeItemId, setActiveItemId] = React.useState(null);

    const {
        loading,
        items,
        total,
        filters,
        setFilters,
        setSortModel,
        deleteItem,
        setPageNo,
        setPageSize,
        refresh
    } = useList({
        getItems: getCodeChallenges,
        initialFilters: BaseFilters,
        initialPageNo: 0,
        initialPageSize: 10,
        itemName: "codeChallenges",
        deleteItemById: deleteCodeChallengeById,
    });

    const handleConfirmationSuccess = () => {
        deleteItem(activeItemId);
        setActiveItemId(null);
    };

    const handleSortModelChange = (newModel) => {
        if (newModel[0]) {
            const { field, sort } = newModel[0];
            setSortModel({ orderBy: field, order: sort });
        }
    };

    const handleCellClick = (girdParam) => {
        if (girdParam.field === 'comment') {
            setOpenDialog(true);
        }
    }

    const columns = React.useMemo(() => {
        return [
            {
                field: 'client',
                headerName: 'Client',
                flex: 1,
                headerClassName: classes.tableHeader,
                valueGetter: (params) => {
                    const { name } = params?.row?.user || 'NOT_AVAILABLE'
                    return name;
                },
            },
            {
                field: 'title',
                headerName: 'Title',
                flex: 1,
                headerClassName: classes.tableHeader,
                valueGetter: (params) => {
                    return params?.row?.title || 'NOT_AVAILABLE'
                },
            },
            {
                field: 'difficulty',
                headerName: 'Difficulty',
                flex: 1,
                headerClassName: classes.tableHeader,
                valueGetter: (params) => {
                    return params?.row?.difficulty || 'NOT_AVAILABLE'
                },
            },
            {
                field: 'createdAt',
                headerName: 'Created At',
                flex: 1,
                type: 'dateTime',
                headerClassName: classes.tableHeader,
                filterable: false,
                valueGetter: (params) => {
                    const date = params?.row?.createdAt
                    return formatDate(date);
                },
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: "actions",
                width: 80,
                headerClassName: classes.tableHeader,
                getActions: (params) =>
                    getListActions(params.id, null, setActiveItemId, "CodeChallenge")
            },
        ];
    }, []);

    return (
        <Box>
            <ContentHeader
                title={"Coding Questions/Challenges"}
                BaseFilters={BaseFilters}
                filters={filters}
                setFilters={setFilters}
            >
                <Button variant='contained'
                    style={{ marginRight: '20px', textTransform: 'none' }}
                    onClick={() => setOpenDialog(true)}
                >
                    Import Challenges
                </Button>
            </ContentHeader>
            <div style={{ height: '80vh', width: '100%', padding: 16 }}>
                <DataGrid
                    pagination
                    autoPageSize
                    paginationMode='server'
                    filterMode='server'
                    density='compact'
                    loading={loading}
                    rowCount={total}
                    onPageChange={setPageNo}
                    onPageSizeChange={setPageSize}
                    sortingMode="server"
                    onSortModelChange={handleSortModelChange}
                    onCellClick={handleCellClick}
                    rows={items}
                    getRowId={(row) => row._id}
                    columns={columns}
                    // onFilterModelChange={handleFilterChange}
                    components={{
                        Toolbar: GridToolbar,
                        LoadingOverlay: LinearProgress,
                        Pagination: ListPagination,
                    }}
                    componentsProps={{
                        pagination: {
                            rowCount: total
                        }
                    }}
                />
            </div>

            <ImportChallengeDialog
                onClose={() => setOpenDialog(false)} open={openDialog}
                onSuccess={(result) => refresh()}
            />

            <ConfirmationDialog
                message={
                    `This Challenge will permanantly get deleted, Are you sure?`
                }
                open={!!activeItemId}
                title="Delete challenge"
                primaryActionVariant="error"
                primaryAction="Yes"
                secondaryAction="Cancel"
                onClose={() => setActiveItemId(null)}
                onSuccess={handleConfirmationSuccess}
            />
        </Box>
    );
}